package com.videoanalyzer.web

import androidx.compose.runtime.*
import com.videoanalyzer.web.api.AnalysisApiResult
import com.videoanalyzer.web.api.ApiClient
import com.videoanalyzer.web.auth.EmailAuthResult
import com.videoanalyzer.web.auth.PasswordResetResult
import com.videoanalyzer.web.auth.SupabaseClient
import com.videoanalyzer.web.components.*
import com.videoanalyzer.web.components.AuthPage
import com.videoanalyzer.web.components.auth.ContactPage
import com.videoanalyzer.web.components.auth.TermsOfServicePage
import com.videoanalyzer.web.components.auth.PrivacyPolicyPage
import com.videoanalyzer.web.screens.HomeScreen
import com.videoanalyzer.web.screens.SettingsScreen
import com.videoanalyzer.web.config.FeatureFlags
import com.videoanalyzer.web.models.*
import com.videoanalyzer.web.state.*
import com.videoanalyzer.web.util.Logger
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.renderComposable

/**
 * Main entry point for the web application.
 *
 * ANDROID EQUIVALENT:
 * This is like your Application class + MainActivity combined.
 * renderComposable is similar to setContent { } in Jetpack Compose.
 */
fun main() {
    renderComposable(rootElementId = "root") {
        App()
    }
}

/**
 * Root composable that manages app state and navigation.
 *
 * ANDROID EQUIVALENT:
 * Like a single-Activity app with a NavHost managing screens.
 * We use state hoisting pattern like Android.
 */
@Composable
fun App() {
    // Debug: Verify app is loading latest code
    println("[App] VideoScore App starting - v2 maintenance mode")

    // Coroutine scope for async operations
    val scope = rememberCoroutineScope()

    // ============================================================
    // State Management
    // ============================================================

    // Auth state
    var authState by remember { mutableStateOf<AuthState>(AuthState.Loading) }

    // Current screen - initialize from URL path
    var currentScreen by remember {
        mutableStateOf(Screen.fromPath(window.location.pathname))
    }

    // Analysis state
    var analysisState by remember { mutableStateOf<AnalysisState>(AnalysisState.Idle) }

    // History state
    var historyState by remember { mutableStateOf<HistoryState>(HistoryState.Loading) }

    // Checkout loading
    var isCheckoutLoading by remember { mutableStateOf(false) }

    // Subscription sync loading
    var isSyncing by remember { mutableStateOf(false) }

    // Account deletion/export state
    var isDeletingAccount by remember { mutableStateOf(false) }
    var isExportingData by remember { mutableStateOf(false) }
    var showDeleteConfirm by remember { mutableStateOf(false) }

    // Auth form state
    var isAuthLoading by remember { mutableStateOf(false) }
    var authErrorMessage by remember { mutableStateOf<String?>(null) }
    var authSuccessMessage by remember { mutableStateOf<String?>(null) }

    // Password reset state
    var recoveryToken by remember { mutableStateOf<String?>(null) }

    // Maintenance mode state
    var isMaintenanceMode by remember { mutableStateOf(false) }
    var maintenanceMessage by remember { mutableStateOf("") }
    var isCheckingMaintenance by remember { mutableStateOf(true) }

    // ============================================================
    // Effects (like LaunchedEffect in Android)
    // ============================================================

    // Check maintenance mode first
    LaunchedEffect(Unit) {
        FeatureFlags.initialize()
        isMaintenanceMode = FeatureFlags.maintenanceMode
        maintenanceMessage = FeatureFlags.maintenanceMessage
        isCheckingMaintenance = false
    }

    // Handle browser back/forward navigation
    LaunchedEffect(Unit) {
        window.onpopstate = { _ ->
            currentScreen = Screen.fromPath(window.location.pathname)
            Unit
        }
    }

    // Check auth on app start (only if not in maintenance mode)
    LaunchedEffect(isMaintenanceMode, isCheckingMaintenance) {
        // Skip auth check if still checking maintenance or in maintenance mode
        if (isCheckingMaintenance || isMaintenanceMode) return@LaunchedEffect
        // Register callback for when session expires (401 from any API call)
        ApiClient.setSessionExpiredCallback {
            Logger.log("Auth", "Session expired - forcing logout")
            SupabaseClient.signOut()
            authState = AuthState.LoggedOut
            currentScreen = Screen.Home
            analysisState = AnalysisState.Idle
        }

        // Check for payment success/cancel in URL
        val urlParams = window.location.search
        val isPaymentSuccess = urlParams.contains("payment=success")
        val isPaymentCancelled = urlParams.contains("payment=cancelled")

        // Clear payment params from URL (clean up)
        if (isPaymentSuccess || isPaymentCancelled) {
            window.history.replaceState(null, "", window.location.pathname)
        }

        // Check for password recovery token in URL
        val parsedRecoveryToken = SupabaseClient.parseRecoveryToken()
        if (parsedRecoveryToken != null) {
            recoveryToken = parsedRecoveryToken
            // Clear the hash from URL (clean up)
            window.history.replaceState(null, "", window.location.pathname)
            authState = AuthState.LoggedOut
            return@LaunchedEffect
        }

        // Check if we have tokens in URL (redirect from Supabase)
        val authResult = SupabaseClient.parseAuthFromUrl()
        if (authResult != null) {
            // Store tokens
            SupabaseClient.storeTokens(
                authResult.accessToken,
                authResult.refreshToken,
                authResult.userId,
                authResult.email
            )
            ApiClient.setToken(authResult.accessToken)

            // Call backend to create/update profile
            val response = ApiClient.authCallback(authResult.userId, authResult.email)
            if (response != null) {
                authState = AuthState.LoggedIn(response.user, response.analysesRemaining)
            } else {
                authState = AuthState.Error("Failed to sync profile")
            }
        } else if (SupabaseClient.isLoggedIn()) {
            // Already logged in, verify token
            val token = SupabaseClient.getAccessToken()
            if (token != null) {
                ApiClient.setToken(token)

                // If returning from payment, wait a moment for webhook to process
                if (isPaymentSuccess) {
                    Logger.log("Payment", "Payment success - waiting for webhook to process...")
                    kotlinx.coroutines.delay(2000)  // Wait 2 seconds for webhook
                }

                val response = ApiClient.getCurrentUser()
                if (response != null) {
                    authState = AuthState.LoggedIn(response.user, response.analysesRemaining)
                    if (isPaymentSuccess) {
                        Logger.log("Payment", "User tier after payment: ${response.user.subscriptionTier}")
                    }
                } else {
                    // Token expired
                    SupabaseClient.signOut()
                    ApiClient.clearToken()
                    authState = AuthState.LoggedOut
                }
            }
        } else {
            authState = AuthState.LoggedOut
        }
    }

    // ============================================================
    // Action Handlers
    // ============================================================

    val handleSignOut: () -> Unit = {
        SupabaseClient.signOut()
        ApiClient.clearToken()
        authState = AuthState.LoggedOut
        currentScreen = Screen.Home
        analysisState = AnalysisState.Idle
    }

    val handleAnalyze: (String) -> Unit = { url ->
        scope.launch {
            analysisState = AnalysisState.Analyzing

            when (val result = ApiClient.analyzeVideo(url)) {
                is AnalysisApiResult.Success -> {
                    analysisState = AnalysisState.Success(
                        analysis = result.response.analysis,
                        isExisting = result.response.isExisting,
                        message = result.response.message,
                        showFullRecommendations = result.response.isFirstAnalysis,
                        totalRecommendationsCount = result.response.totalRecommendationsCount,
                        freeRecommendationsLimit = result.response.freeRecommendationsLimit
                    )
                    // Update remaining analyses
                    val currentAuth = authState
                    if (currentAuth is AuthState.LoggedIn && !result.response.isExisting) {
                        val newRemaining = if (currentAuth.analysesRemaining > 0) {
                            currentAuth.analysesRemaining - 1
                        } else {
                            currentAuth.analysesRemaining
                        }
                        authState = currentAuth.copy(analysesRemaining = newRemaining)
                    }
                }
                is AnalysisApiResult.LimitReached -> {
                    analysisState = AnalysisState.LimitReached(result.currentUsage, result.limit)
                }
                is AnalysisApiResult.Error -> {
                    analysisState = AnalysisState.Error(result.message)
                }
                is AnalysisApiResult.NotAuthenticated -> {
                    authState = AuthState.LoggedOut
                    analysisState = AnalysisState.Idle
                }
            }
        }
    }

    val handleLoadHistory: () -> Unit = {
        scope.launch {
            historyState = HistoryState.Loading
            val result = ApiClient.getHistory()
            result.fold(
                onSuccess = { response ->
                    historyState = HistoryState.Loaded(
                        response.analyses,
                        response.total,
                        response.hasMore
                    )
                },
                onFailure = { error ->
                    historyState = HistoryState.Error(error.message ?: "Failed to load history")
                }
            )
        }
    }

    val handleViewAnalysis: (String) -> Unit = { analysisId ->
        scope.launch {
            analysisState = AnalysisState.Analyzing
            val result = ApiClient.getAnalysis(analysisId)
            result.fold(
                onSuccess = { analysis ->
                    // Check if user is Pro - if so, show full; otherwise gate
                    val currentAuth = authState
                    val isPro = currentAuth is AuthState.LoggedIn &&
                            currentAuth.user.subscriptionTier == "pro"

                    val totalRecs = analysis.topRecommendations.size +
                            analysis.titleAnalysis.suggestions.size +
                            (analysis.thumbnailAnalysis?.suggestions?.size ?: 0)

                    analysisState = AnalysisState.Success(
                        analysis = analysis,
                        isExisting = true,
                        message = null,
                        showFullRecommendations = isPro,
                        totalRecommendationsCount = totalRecs,
                        freeRecommendationsLimit = 2
                    )
                    currentScreen = Screen.Home
                },
                onFailure = { error ->
                    analysisState = AnalysisState.Error(error.message ?: "Analysis not found")
                }
            )
        }
    }

    val handleUpgrade: () -> Unit = {
        // Navigate to Pricing page instead of directly to Stripe
        currentScreen = Screen.Pricing
    }

    val handleSubscribe: () -> Unit = {
        scope.launch {
            isCheckoutLoading = true
            val successUrl = "${window.location.origin}?payment=success"
            val cancelUrl = "${window.location.origin}?payment=cancelled"

            val result = ApiClient.createCheckout(successUrl, cancelUrl)
            result.fold(
                onSuccess = { response ->
                    window.location.href = response.checkoutUrl
                },
                onFailure = {
                    isCheckoutLoading = false
                    Logger.error("Checkout error", it)
                }
            )
        }
    }

    val handleManageSubscription: () -> Unit = {
        scope.launch {
            isCheckoutLoading = true
            val returnUrl = window.location.origin

            val result = ApiClient.createPortal(returnUrl)
            result.fold(
                onSuccess = { portalUrl ->
                    window.location.href = portalUrl
                },
                onFailure = {
                    isCheckoutLoading = false
                    Logger.error("Portal error", it)
                }
            )
        }
    }

    val handleSyncSubscription: () -> Unit = {
        scope.launch {
            isSyncing = true
            val result = ApiClient.syncSubscription()
            result.fold(
                onSuccess = { updatedProfile ->
                    // Update auth state with new profile
                    val currentAuth = authState
                    if (currentAuth is AuthState.LoggedIn) {
                        authState = currentAuth.copy(user = updatedProfile)
                    }
                    Logger.log("Subscription", "Subscription synced successfully")
                },
                onFailure = {
                    Logger.error("Sync error", it)
                }
            )
            isSyncing = false
        }
    }

    val handleBuyCredits: (String) -> Unit = { packId ->
        scope.launch {
            isCheckoutLoading = true
            val successUrl = "${window.location.origin}?payment=success"
            val cancelUrl = "${window.location.origin}?payment=cancelled"

            val result = ApiClient.buyCredits(packId, successUrl, cancelUrl)
            result.fold(
                onSuccess = { response ->
                    // Redirect to Stripe Checkout
                    window.location.href = response.checkoutUrl
                },
                onFailure = {
                    isCheckoutLoading = false
                    Logger.error("Credit purchase error", it)
                }
            )
        }
    }

    val handleDeleteAccount: () -> Unit = {
        scope.launch {
            isDeletingAccount = true
            val result = ApiClient.deleteAccount()
            result.fold(
                onSuccess = {
                    // Sign out and redirect to landing page
                    SupabaseClient.signOut()
                    authState = AuthState.LoggedOut
                    currentScreen = Screen.Home
                    showDeleteConfirm = false
                },
                onFailure = { error ->
                    Logger.error("Account deletion failed", error)
                    // Could show error message to user here
                }
            )
            isDeletingAccount = false
        }
    }

    val handleExportData: () -> Unit = {
        scope.launch {
            isExportingData = true
            val result = ApiClient.exportData()
            result.fold(
                onSuccess = { data ->
                    // Trigger download
                    val blob = org.w3c.files.Blob(arrayOf(data), org.w3c.files.BlobPropertyBag("application/json"))
                    val url = org.w3c.dom.url.URL.createObjectURL(blob)
                    val link = kotlinx.browser.document.createElement("a") as org.w3c.dom.HTMLAnchorElement
                    link.href = url
                    link.download = "videoscore-data-export.json"
                    link.click()
                    org.w3c.dom.url.URL.revokeObjectURL(url)
                },
                onFailure = { error ->
                    Logger.error("Data export failed", error)
                }
            )
            isExportingData = false
        }
    }

    val handleNavigate: (Screen) -> Unit = { screen ->
        currentScreen = screen
        // Update browser URL
        val path = Screen.toPath(screen)
        window.history.pushState(null, "", path)
        // Scroll to top when navigating
        window.scrollTo(0.0, 0.0)
        // Load data for specific screens
        when (screen) {
            is Screen.History -> handleLoadHistory()
            else -> {}
        }
    }

    // ============================================================
    // UI Rendering
    // ============================================================

    // Show maintenance page if enabled
    if (isCheckingMaintenance) {
        FullPageLoading("Loading...")
        return
    }

    if (isMaintenanceMode) {
        MaintenancePage(message = maintenanceMessage)
        return
    }

    // Main layout
    Div({
        style {
            minHeight(100.vh)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        when (val auth = authState) {
            is AuthState.Loading -> {
                FullPageLoading("Checking authentication...")
            }

            is AuthState.LoggedOut -> {
                // Determine initial page based on URL
                val initialAuthPage = when (currentScreen) {
                    is Screen.Terms -> AuthPage.TermsOfService
                    is Screen.Privacy -> AuthPage.PrivacyPolicy
                    is Screen.Contact -> AuthPage.Contact
                    else -> AuthPage.Landing
                }
                AuthScreen(
                    initialPage = initialAuthPage,
                    onSignInWithGoogle = { SupabaseClient.signInWithGoogle() },
                    onSignInWithEmail = { email, password ->
                        scope.launch {
                            isAuthLoading = true
                            authErrorMessage = null
                            authSuccessMessage = null

                            when (val result = SupabaseClient.signInWithEmailPassword(email, password)) {
                                is EmailAuthResult.Success -> {
                                    ApiClient.setToken(result.accessToken)
                                    val response = ApiClient.authCallback(result.userId, result.email)
                                    if (response != null) {
                                        authState = AuthState.LoggedIn(response.user, response.analysesRemaining)
                                    } else {
                                        authErrorMessage = "Failed to sync profile"
                                    }
                                }
                                is EmailAuthResult.ConfirmationRequired -> {
                                    authSuccessMessage = result.message
                                }
                                is EmailAuthResult.Error -> {
                                    authErrorMessage = result.message
                                }
                            }
                            isAuthLoading = false
                        }
                    },
                    onSignUpWithEmail = { email, password ->
                        scope.launch {
                            isAuthLoading = true
                            authErrorMessage = null
                            authSuccessMessage = null

                            when (val result = SupabaseClient.signUpWithEmailPassword(email, password)) {
                                is EmailAuthResult.Success -> {
                                    ApiClient.setToken(result.accessToken)
                                    val response = ApiClient.authCallback(result.userId, result.email)
                                    if (response != null) {
                                        authState = AuthState.LoggedIn(response.user, response.analysesRemaining)
                                    } else {
                                        authErrorMessage = "Failed to create profile"
                                    }
                                }
                                is EmailAuthResult.ConfirmationRequired -> {
                                    authSuccessMessage = result.message
                                }
                                is EmailAuthResult.Error -> {
                                    authErrorMessage = result.message
                                }
                            }
                            isAuthLoading = false
                        }
                    },
                    onForgotPassword = { email ->
                        scope.launch {
                            isAuthLoading = true
                            authErrorMessage = null
                            authSuccessMessage = null

                            when (val result = SupabaseClient.requestPasswordReset(email)) {
                                is PasswordResetResult.Success -> {
                                    authSuccessMessage = "Password reset email sent! Check your inbox."
                                }
                                is PasswordResetResult.Error -> {
                                    authErrorMessage = result.message
                                }
                            }
                            isAuthLoading = false
                        }
                    },
                    onResetPassword = { newPassword ->
                        scope.launch {
                            isAuthLoading = true
                            authErrorMessage = null
                            authSuccessMessage = null

                            val token = recoveryToken
                            if (token == null) {
                                authErrorMessage = "Invalid or expired reset link. Please request a new one."
                                isAuthLoading = false
                                return@launch
                            }

                            when (val result = SupabaseClient.updatePassword(token, newPassword)) {
                                is PasswordResetResult.Success -> {
                                    authSuccessMessage = "Password updated successfully! You can now sign in."
                                    recoveryToken = null // Clear the token
                                }
                                is PasswordResetResult.Error -> {
                                    authErrorMessage = result.message
                                }
                            }
                            isAuthLoading = false
                        }
                    },
                    isLoading = isAuthLoading,
                    errorMessage = authErrorMessage,
                    successMessage = authSuccessMessage,
                    recoveryToken = recoveryToken
                )
            }

            is AuthState.Error -> {
                ErrorScreen(auth.message) {
                    authState = AuthState.LoggedOut
                }
            }

            is AuthState.LoggedIn -> {
                // Header
                Header(
                    user = auth.user,
                    currentScreen = currentScreen,
                    analysesRemaining = auth.analysesRemaining,
                    onNavigate = handleNavigate,
                    onSignOut = handleSignOut
                )

                // Main content
                Main({
                    style {
                        property("flex", "1")
                        padding(24.px)
                    }
                }) {
                    when (currentScreen) {
                        is Screen.Home -> {
                            HomeScreen(
                                analysisState = analysisState,
                                onAnalyze = handleAnalyze,
                                onNewAnalysis = { analysisState = AnalysisState.Idle },
                                onUpgrade = handleUpgrade,
                                onBuyCredits = handleBuyCredits,
                                isCheckoutLoading = isCheckoutLoading
                            )
                        }

                        is Screen.History -> {
                            when (val history = historyState) {
                                is HistoryState.Loading -> {
                                    LoadingSpinner("Loading history...")
                                }
                                is HistoryState.Loaded -> {
                                    HistoryScreen(
                                        analyses = history.analyses,
                                        total = history.total,
                                        hasMore = history.hasMore,
                                        isLoading = false,
                                        onViewAnalysis = handleViewAnalysis,
                                        onLoadMore = { /* TODO: pagination */ }
                                    )
                                }
                                is HistoryState.Error -> {
                                    ErrorMessage(history.message)
                                }
                            }
                        }

                        is Screen.Pricing -> {
                            PricingScreen(
                                currentTier = auth.user.subscriptionTier,
                                analysesUsed = auth.user.analysesThisMonth,
                                creditBalance = auth.user.creditBalance,
                                isLoading = isCheckoutLoading,
                                onUpgrade = handleSubscribe,
                                onManageSubscription = handleManageSubscription,
                                onBuyCredits = handleBuyCredits
                            )
                        }

                        is Screen.Settings -> {
                            SettingsScreen(
                                user = auth.user,
                                onManageSubscription = handleManageSubscription,
                                onRefreshSubscription = handleSyncSubscription,
                                onDeleteAccount = handleDeleteAccount,
                                onExportData = handleExportData,
                                isLoading = isCheckoutLoading,
                                isSyncing = isSyncing,
                                isDeletingAccount = isDeletingAccount,
                                isExportingData = isExportingData,
                                showDeleteConfirm = showDeleteConfirm,
                                onShowDeleteConfirm = { showDeleteConfirm = it }
                            )
                        }

                        is Screen.AnalysisDetail -> {
                            // Handled via handleViewAnalysis
                        }

                        is Screen.Contact -> {
                            ContactPage()
                        }

                        is Screen.Terms -> {
                            TermsOfServicePage(onBack = { handleNavigate(Screen.Home) })
                        }

                        is Screen.Privacy -> {
                            PrivacyPolicyPage(onBack = { handleNavigate(Screen.Home) })
                        }
                    }
                }

                // Footer
                Footer(onNavigate = handleNavigate)
            }
        }

        // Cookie consent banner (shows on all screens if consent not given)
        CookieConsentBanner()
    }
}

/**
 * Error screen.
 */
@Composable
private fun ErrorScreen(message: String, onRetry: () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            minHeight(100.vh)
            padding(24.px)
        }
    }) {
        Div({
            style { fontSize(48.px) }
        }) { Text("😕") }

        H1({
            style {
                fontSize(24.px)
                color(Color("#f8fafc"))
                marginTop(16.px)
                marginBottom(8.px)
            }
        }) { Text("Something went wrong") }

        P({
            style {
                color(Color("#94a3b8"))
                marginBottom(24.px)
            }
        }) { Text(message) }

        Button({
            classes("btn", "btn-primary")
            onClick { onRetry() }
        }) { Text("Try Again") }
    }
}

/**
 * Error message inline.
 */
@Composable
private fun ErrorMessage(message: String) {
    Div({
        classes("card")
        style {
            backgroundColor(Color("rgba(239, 68, 68, 0.1)"))
            property("border-color", "#ef4444")
            textAlign("center")
        }
    }) {
        P({
            style { color(Color("#ef4444")) }
        }) { Text(message) }
    }
}

/**
 * Footer component.
 */
@Composable
private fun Footer(onNavigate: (Screen) -> Unit) {
    Footer({
        style {
            property("border-top", "1px solid rgba(255, 255, 255, 0.05)")
            padding(32.px, 24.px)
            textAlign("center")
            property("background", "linear-gradient(180deg, transparent 0%, rgba(15, 23, 42, 0.5) 100%)")
        }
    }) {
        // Footer content
        Div({
            style {
                maxWidth(1200.px)
                margin(0.px, "auto".unsafeCast<CSSNumeric>())
            }
        }) {
            // Brand
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(10.px)
                    marginBottom(16.px)
                }
            }) {
                Logo(size = LogoSize.SMALL)
                LogoText(size = LogoSize.SMALL)
            }

            // Tagline
            P({
                style {
                    color(Color("#64748b"))
                    fontSize(13.px)
                    marginBottom(16.px)
                }
            }) {
                Text("AI-powered insights for YouTube creators")
            }

            // Legal Links
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(24.px)
                    marginBottom(16.px)
                }
            }) {
                Span({
                    style {
                        color(Color("#64748b"))
                        fontSize(13.px)
                        cursor("pointer")
                        property("transition", "color 0.2s")
                    }
                    onClick { onNavigate(Screen.Terms) }
                    onMouseOver { (it.target as? org.w3c.dom.HTMLElement)?.style?.color = "#94a3b8" }
                    onMouseOut { (it.target as? org.w3c.dom.HTMLElement)?.style?.color = "#64748b" }
                }) { Text("Terms of Service") }

                Span({
                    style {
                        color(Color("#64748b"))
                        fontSize(13.px)
                        cursor("pointer")
                        property("transition", "color 0.2s")
                    }
                    onClick { onNavigate(Screen.Privacy) }
                    onMouseOver { (it.target as? org.w3c.dom.HTMLElement)?.style?.color = "#94a3b8" }
                    onMouseOut { (it.target as? org.w3c.dom.HTMLElement)?.style?.color = "#64748b" }
                }) { Text("Privacy Policy") }

                Span({
                    style {
                        color(Color("#64748b"))
                        fontSize(13.px)
                        cursor("pointer")
                        property("transition", "color 0.2s")
                    }
                    onClick { onNavigate(Screen.Contact) }
                    onMouseOver { (it.target as? org.w3c.dom.HTMLElement)?.style?.color = "#94a3b8" }
                    onMouseOut { (it.target as? org.w3c.dom.HTMLElement)?.style?.color = "#64748b" }
                }) { Text("Contact") }
            }

            // Copyright
            P({
                style {
                    color(Color("#475569"))
                    fontSize(12.px)
                }
            }) {
                val currentYear = kotlin.js.Date().getFullYear().toInt()
                Text("© $currentYear VideoScore. All rights reserved.")
            }
        }
    }
}
